<template>
    <filters-wrapper :has-active-filters="hasActiveFilters" @submit="submit" @reset="reset">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="submit" />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name_or_surname')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="client-name"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.managerId"
                        item-title="name"
                        item-value="personId"
                        :items="users"
                        :label="$t('manager')"
                        :loading="isLoadingData"
                        :disabled="isLoadingData"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="manager-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.rangeType"
                        :items="rangeType"
                        :label="$t('date')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="date-range-type"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <daterange
                        :start-date-label="$t('date_from')"
                        :end-date-label="$t('date_to')"
                        :dense="true"
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.rangeDateFrom"
                        :_end-date="filters.rangeDateTo"
                        data-cy="date-range"
                        :clearable="true"
                        @change-start-date="filters.rangeDateFrom = $event"
                        @change-end-date="filters.rangeDateTo = $event"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.email"
                        :label="$t('email')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="email"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.b2CId"
                        :item-title="getB2CName"
                        item-value="b2cId"
                        :items="b2cs"
                        :label="'B2C'"
                        :loading="isLoadingData"
                        :disabled="isLoadingData"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="b2c-id"
                    />
                </v-col>
                <v-col cols="12" md="2" sm="6">
                    <v-select
                        v-model="filters.birthdayMonthFrom"
                        :items="months"
                        :label="`${$t('birthday')} (${$t('from_number')}) - ${$t('month')}`"
                        persistent-hint
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="birthday-month-from"
                    />
                </v-col>
                <v-col cols="12" md="1" sm="6">
                    <v-select
                        v-model="filters.birthdayDayFrom"
                        :items="filters.birthdayMonthFrom ? getDays(filters.birthdayMonthFrom) : []"
                        :label="$t('day')"
                        :disabled="!filters.birthdayMonthFrom"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="birthday-day-from"
                    />
                </v-col>
                <v-col cols="12" md="2" sm="6">
                    <v-select
                        v-model="filters.birthdayMonthTo"
                        :items="months"
                        :label="`${$t('birthday')} (${$t('to_number')}) - ${$t('month')}`"
                        persistent-hint
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="birthday-month-to"
                    />
                </v-col>
                <v-col cols="12" md="1" sm="6">
                    <v-select
                        v-model="filters.birthdayDayTo"
                        :items="filters.birthdayMonthTo ? getDays(filters.birthdayMonthTo) : []"
                        :label="$t('day')"
                        :disabled="!filters.birthdayMonthTo"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="birthday-day-to"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.countryId"
                        :items="countries"
                        :label="$t('country')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="country-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.memberLevel"
                        :item-title="getMembershipLevel"
                        item-value="id"
                        :items="membershipLevels"
                        :label="$t('membership_level')"
                        :loading="isLoadingData"
                        :disabled="isLoadingData"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="membership-level-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.reservationsNumberFrom"
                        :label="`${$t('number_of_reservations')} (${$t('from_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="reservations-number-from"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.reservationsNumberTo"
                        :label="`${$t('number_of_reservations')} (${$t('to_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="reservations-number-to"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.cityId"
                        :items="cities"
                        :label="$t('city')"
                        :disabled="isCitiesLoading || !cities.length"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="city-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.language"
                        :items="locales"
                        :label="$t('language')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="language"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.currentBalanceFrom"
                        :label="`${$t('current_balance')} (${$t('from_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="current-balance-from"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.currentBalanceTo"
                        :label="`${$t('current_balance')} (${$t('to_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="current-balance-to"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col v-if="!isAgency && !isTO2 && isCRMExtendedModule" cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.stage"
                        :items="stages"
                        :label="$t('stage')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="stage"
                    />
                </v-col>
                <v-col v-if="isCRMExtendedModule" cols="12" md="3" sm="6">
                    <v-combobox
                        v-model="filters.labels"
                        :label="$t('labels')"
                        chips
                        multiple
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="labels"
                    >
                        <template v-slot:chip="{attrs, item}">
                            <v-chip v-bind="attrs" closable color="primary" @click:close="removeLabel(item.value)">
                                <strong>{{ item.title }}</strong>
                            </v-chip>
                        </template>
                    </v-combobox>
                </v-col>
                <v-col
                    v-if="!isAgency && !isTO2 && $conf.public.reservationFilters.loyaltyPoints"
                    cols="12"
                    md="3"
                    sm="6"
                >
                    <v-text-field
                        v-model="filters.loyaltyPointFrom"
                        :label="`${$t('loyalty_points')} (${$t('from_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="loyalty-point-to"
                    />
                </v-col>
                <v-col
                    v-if="!isAgency && !isTO2 && $conf.public.reservationFilters.loyaltyPoints"
                    cols="12"
                    md="3"
                    sm="6"
                >
                    <v-text-field
                        v-model="filters.loyaltyPointTo"
                        :label="`${$t('loyalty_points')} (${$t('to_number')})`"
                        type="number"
                        hide-spin-buttons
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="loyalty-point-to"
                    />
                </v-col>
            </v-row>
            <v-row v-if="!isAgency && !isTO2 && isCRMExtendedModule" dense>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.priority"
                        :items="priority"
                        :label="$t('priority')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="priority"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import Daterange from '@/components/search/forms/Daterange.vue'
    import {Priority, Stage, DataRange, type SelectOptionType} from '~/utils/types/types'

    @Component({
        components: {Daterange},
    })
    export class ClientsFilters extends Vue {
        @VModel() filters
        @Prop({required: true, type: Boolean}) hasActiveFilters: boolean
        @Prop({required: true, type: Boolean}) isLoadingData: boolean
        @Prop({required: true, default: []}) users
        @Prop({required: true, default: []}) b2cs
        @Prop({required: true, default: []}) membershipLevels
        @Emit()
        submit() {}

        @Emit()
        reset() {}

        @Watch('filters.countryId')
        async onLocaleChange(): Promise<void> {
            this.filters.cityId = null
            if (this.filters.countryId) {
                try {
                    this.isCitiesLoading = true
                    const rs = await this.$api.locations.get({
                        limitCities: -1,
                        orderBy: 'ALPHABET',
                        countryId: this.filters.countryId,
                    })
                    if (rs.cities) {
                        this.cities = rs.cities.map(({id, name}) => {
                            return {title: name, value: id}
                        })
                    }
                } catch (e) {
                    this.cities = []
                } finally {
                    this.isCitiesLoading = false
                }
            } else this.cities = []
        }

        cities: SelectOptionType[] = []
        isCitiesLoading: boolean = true

        get locales(): SelectOptionType[] {
            return Object.values(this.$conf.public.locales).map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`)}
            })
        }

        get countries(): SelectOptionType[] {
            return runtimeStore.countries.map(({id, name}) => {
                return {title: name, value: id}
            })
        }

        get priority(): SelectOptionType[] {
            const priorities: Priority[] = [Priority.VIP, Priority.High, Priority.Normal, Priority.Low]
            return priorities.map(value => ({value, title: this.$t(`crmPriority.${value}`)}))
        }

        get stages(): SelectOptionType[] {
            const stages: Stage[] = [Stage.Client, Stage.Potential]
            return stages.map(value => ({value, title: this.$t(`crmStage.${value}`)}))
        }

        get rangeType(): SelectOptionType[] {
            const dateTypes: DataRange[] = [
                DataRange.Registration,
                DataRange.Expiration,
                DataRange.FirstReservation,
                DataRange.LastReservation,
            ]
            return dateTypes.map(value => ({value, title: this.$t(`date_types.${value}`)}))
        }

        get months(): SelectOptionType[] {
            const months = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
            ]
            return months.map(value => ({value, title: this.$t(`months.${value}`)}))
        }

        getDays(month: string): {value: number; title: number}[] {
            const monthDays = {
                January: 31,
                February: 29,
                March: 31,
                April: 30,
                May: 31,
                June: 30,
                July: 31,
                August: 31,
                September: 30,
                October: 31,
                November: 30,
                December: 31,
            }
            const daysLength = monthDays[month]
            return [...Array(daysLength)].map((_, i) => ({value: i + 1, title: i + 1}))
        }

        get personId(): number {
            return authStore.person.personId
        }

        getMembershipLevel({code}): string {
            return this.$t(`crm_membership_level.${code}`)
        }

        getB2CName({name, id}): string {
            return `${name || `id: ${id}`}`
        }

        removeLabel(item) {
            this.filters.labels = this.filters.labels.filter(label => item !== label)
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }
    }

    export default ClientsFilters
</script>
